import React from 'react'

import { LockOpen } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

import { useColorMode } from 'hooks'
// Sibling paths, not the `components` barrel: this file is exported FROM that barrel, so
// going through it would be a cycle. Same reason `ExampleKindBadge` states — and the same
// reason `InfoTooltip` below is reached by path.
import { STATUS_TOKEN_H, statusTone } from '../StatusChip/StatusChip'
import { InfoTooltip } from '../../feedback/InfoTooltip'

/**
 * VRG-491 — "the tamper loop on this tag has been cut", beside the certificate's status.
 *
 * ## Why a badge, when invalid scans get an icon and a count
 *
 * Tamper is a STATE: durable, binary, one per certificate. The NTAG 424 TT loop is a
 * physical conductor and cutting it is irreversible, so this is something the object IS,
 * which is the same kind of thing Active / Lost / In transfer are. An invalid scan is an
 * EVENT and a tally of events is not a state, which is why that indicator is deliberately
 * not a badge (see `InvalidScanIndicator`).
 *
 * ## ⚠️ THREE states, not two — and NULL must render NOTHING
 *
 * `scans.tampered` is nullable and the third value is not "probably fine":
 *
 * | value   | means                                          | renders |
 * |---------|------------------------------------------------|---------|
 * | `true`  | the loop is cut                                 | this badge |
 * | `false` | the loop is intact                              | nothing |
 * | `NULL`  | the tag CANNOT detect tampering, or was unreadable | nothing |
 *
 * NULL is not an edge case: it is 93% of all scans locally, because most tags carry no
 * tamper loop at all. Rendering it as "intact" would have the product assert a physical
 * guarantee the hardware never made — a claim about an object nobody checked — which on a
 * provenance product is the worst available failure.
 *
 * The component is therefore deliberately asymmetric: it renders on `true` and on nothing
 * else, and there is **no** "Sealed"/"Intact" counterpart to pair with it. A negative badge
 * would need to distinguish `false` from NULL to be honest, and the certificate payload
 * flattens both to `is_tag_tampered === false` upstream (`certificate-data.aggregate.ts`
 * derives it as `tampered_scan_count > 0`, and that count skips NULL rows precisely so an
 * unreadable scan cannot clear a real tamper). So the only truthful thing to draw for a
 * falsy value is nothing at all.
 *
 * ## Tone: neutral, not red
 *
 * VERA-628 settled this for the equivalent chip in the Scans table and the wording carries
 * over unchanged ("Opened", an open padlock, neutral): a cut loop means the seal was
 * opened, and on a closure tag opening it is the EXPECTED outcome of ordinary use. It is a
 * fact about the seal, not a failed verification, and colour on this page is reserved for
 * verification outcomes.
 *
 * ## This pill is now the WHOLE signal — there is no tamper alert beside it
 *
 * ⚠️ An earlier version of this note ended "the certificate page still raises its existing
 * warning Alert for the cases that warrant alarm". That is no longer true, and the sentence
 * described the defect rather than a design: the page rendered this neutral grey pill AND a
 * red "Tag tampering detected" banner asserting the tag "has been physically tampered with",
 * so one signal was given two contradictory registers and the louder one was the wrong one.
 * The banner is deleted (see the standing comment where it used to live in
 * `pages/main/CertificatePage`).
 *
 * The reasoning is `invalidScanSeverity.ts`'s, applied to hardware instead of key material:
 * a broken loop is a fact the tag can report, and "who broke it, and why" is a question it
 * structurally cannot answer. The ordinary cause is the owner opening what they bought.
 * Red on that is a false accusation the product cannot support, and spending red on
 * ambiguity degrades it everywhere it does mean act-now.
 *
 * So the badge's job is to state the fact beside the status, and the TOOLTIP carries the
 * nuance the banner was overclaiming — which is also why the definition is not retyped
 * here. It is `scan-status-opened` in `docs/glossary.json`, read through `InfoTooltip`
 * (working-conventions §44), so this pill, the Scans-tab chip and the glossary page cannot
 * drift into three different accounts of the same word. Only INVALID SCANS raise a red
 * alert on the certificate page.
 *
 * ## A PILL, matching the VERA-260 Analytics scan-status pills
 *
 * REVERSED 2026-08-30 by user directive: "Opened should be a pill like 260 analytics we
 * have built." This section previously argued the opposite (a rectangle), on the grounds
 * that two pills side by side read as two values of ONE axis ("is this Active or
 * Opened?") when the truth is that a certificate is Active AND Opened. That reasoning is
 * recorded rather than deleted because it names a real risk, and the risk is now carried
 * by three other channels instead of silhouette: the open-padlock GLYPH (the status chip
 * beside it has none), the NEUTRAL tone (every certificate status carries a status hue),
 * and the word itself. Do not "fix" this back to a rectangle.
 *
 * The treatment is `StatusPill`'s `pill` variant from VERA-260: 999px radius, 16px
 * horizontal padding, 13px/500 label, 16px glyph, 8px gap, over the SAME `statusTone`
 * ramp this component already used, so the pill and the status chip beside it can never
 * drift in light/dark.
 *
 * ## Why this does not import `StatusPill` itself
 *
 * Two reasons, and the second outlives the first.
 *
 * 1. `StatusPill` does not exist on this branch. It lives only on
 *    `agent/VERA-260-Platform.Analytics.Overview`, which is unmerged and not an ancestor
 *    of `origin/main`, so importing it would not compile here.
 * 2. Even once it merges, it is the WRONG component to reuse. `StatusPill` is a real
 *    `<button>` carrying `aria-pressed`, built for the filters drawer and the chart
 *    legend, where the pill TOGGLES something. This is a static status token: it is not
 *    pressable, and rendering it as a pressed-state button would announce a fact to a
 *    screen reader as an interactive control.
 *
 * So the shared thing here is the VISUAL TREATMENT, not the widget. When VERA-260 lands,
 * the right convergence (working-conventions section 36) is to extract the pill geometry
 * + tone that `StatusPill` and this component both spend, and have each consume it:
 * `StatusPill` adding the button/toggle behaviour on top, this staying static. Extracting
 * that now would create the second copy on a branch that cannot see the first.
 *
 * TODO(VERA-260 / StatusPill): once VERA-260 merges, extract the shared pill geometry +
 * statusTone treatment and consume it from BOTH StatusPill and TamperBadge.
 */

export interface TamperBadgeProps {
  /**
   * The certificate's `is_tag_tampered`. **Renders nothing for false, null or undefined**
   * (see the three-state table above), so call sites pass the field straight through
   * without a guard.
   */
  tampered?: boolean | null
  /** `dense` for a table row's metadata line — 20px tall, 11px label. */
  size?: 'default' | 'dense'
}

export const TamperBadge = ({ tampered, size = 'default' }: TamperBadgeProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'

  // The ONLY truthy branch. `false` and NULL both land here and both render nothing —
  // see the three-state table above for why that is a correctness requirement and not a
  // convenience.
  if (tampered !== true) return null

  const dense = size === 'dense'
  // Neutral, via the shared status tone so light/dark can never drift from the pills
  // beside it. Same hue family the Scans table's "Opened" chip uses (VERA-628).
  const { color, border, background } = statusTone(
    { color: theme.palette.primary[900] ?? '#27272A' },
    isDark,
  )

  return (
    /*
     * The definition comes from the glossary, never a sentence typed here (§44). `term` is
     * enough on its own: `scan-status-opened` is a `brand`-floor entry, so it resolves for
     * every reader who can see this pill — unlike `ExampleKindBadge`, which has to pass a
     * `text` fallback beside its `term` because `demo` is `audience: internal` and returns
     * nothing for a brand. No fallback is wanted here; a retyped body is exactly the drift
     * the single source exists to prevent.
     *
     * The entry is the SCAN status, reused deliberately rather than forked into a
     * certificate-level twin. It is the same signal seen from one level up (this pill lights
     * when any scan has reported it) and the entry already carries `tampered` / `tamper` /
     * `opened seal` as aliases, so a second "Opened (certificate)" entry would be two
     * definitions of one word — §44's whole failure mode. Its `long` names this pill
     * explicitly so a reader arriving from the certificate header sees themselves in it.
     *
     * The `Stack` is passed straight through as the Tooltip child, as `ExampleKindBadge`
     * does: MUI needs a child that holds a ref and forwards DOM props, and a MUI `Stack`
     * does both, so no wrapper span is needed.
     */
    <InfoTooltip term="scan-status-opened" placement="top">
    <Stack
      direction="row"
      alignItems="center"
      // 8px, matching StatusPill's `spacing={2}`. Was 4px, which crowded the glyph
      // against the label once the pill gained horizontal room.
      spacing={dense ? 1.5 : 2}
      sx={{
        // HEIGHT is the one place this deliberately departs from StatusPill, which is
        // padding-driven (`py: 2.5`) and lands ~25px. This badge sits inline with the
        // certificate's status chip, which is STATUS_TOKEN_H (28), and a 3px height
        // difference between two adjacent tokens reads as a misalignment rather than as
        // a distinction. A 999px radius clamps to half the height, so pinning the height
        // costs nothing: it is still a true pill, just one that lines up with its
        // neighbour.
        height: dense ? 20 : STATUS_TOKEN_H,
        // 16px, StatusPill's `px: 4`. The old 8px was rectangle padding; a pill needs the
        // extra room or the rounded ends crowd the first and last glyph.
        px: dense ? 2.5 : 4,
        flexShrink: 0,
        // `help`, never `pointer` — the same call `StatusChip` makes and for the same
        // reason: the dense pill sits in a Certificates row that DOES navigate on click, so
        // `pointer` would promise a destination this pill does not have. `help` advertises
        // that there is something to read on hover, which matters more here than it does on
        // `ExampleKindBadge` (which uses `default`): the banner that used to explain this
        // signal is gone, so the tooltip is now the ONLY place the explanation exists.
        cursor: 'help',
        color,
        backgroundColor: background,
        border: `1px solid ${border}`,
        // The directive. 999px rather than a computed half-height so it stays a pill at
        // both sizes without either one restating the arithmetic.
        borderRadius: '999px',
      }}
    >
      <LockOpen size={dense ? 12 : 16} strokeWidth={2} />
      <Typography
        sx={{
          fontSize: dense ? 11 : 13,
          fontWeight: 500,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        Opened
      </Typography>
    </Stack>
    </InfoTooltip>
  )
}
